import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router, usePage } from "@inertiajs/react";
import {
  adminUserAdminRolePath,
  adminUserAdmissionPath,
  adminUserPath,
  adminUsersPath,
  adminUserWaitlistingPath,
} from "@travily/routes";
import type AdminUsersIndex from "@travily/types/pages/AdminUsersIndex";
import type { ReactNode } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminListNavigation } from "@/hooks/admin/use-admin-list-navigation";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import type { SharedProps } from "@/types";
import { IndexView } from "./index.view";

type AdminUsersIndexPageProps = AdminUsersIndex;

function AdminUsersIndexPage({ users, pagy }: AdminUsersIndexPageProps) {
  const account = useAdminLayoutAccount();
  const { current_user } = usePage<SharedProps>().props;

  const handleActivateUser = (userId: number) => {
    router.post(adminUserAdmissionPath(userId));
  };

  const handleDeactivateUser = (userId: number) => {
    router.post(adminUserWaitlistingPath(userId));
  };

  const handleToggleAdmin = (userId: number) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const path = adminUserAdminRolePath(userId);
    if (user.admin) {
      router.delete(path);
    } else {
      router.post(path);
    }
  };

  const handleDeleteUser = (userId: number, force: boolean) => {
    router.delete(adminUserPath(userId), { data: { force }, preserveScroll: true });
  };

  const { handlePageChange } = useAdminListNavigation({ basePath: adminUsersPath() });

  const userCount = formatCount(pagy.count, "user");

  return (
    <AdminLayout activeUrl={adminUsersPath()} account={account}>
      <Head title="Users - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Users"
        titleBadge={
          <Badge color="gray" size="sm">
            {userCount}
          </Badge>
        }
        subtitle="All registered users"
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          users={users}
          currentUserId={current_user?.id ?? 0}
          pagy={pagy}
          onActivateUser={handleActivateUser}
          onDeactivateUser={handleDeactivateUser}
          onPageChange={handlePageChange}
          onToggleAdmin={handleToggleAdmin}
          onDeleteUser={handleDeleteUser}
        />
      </div>
    </AdminLayout>
  );
}

AdminUsersIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminUsersIndexPage;
